import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, ArrowLeft, ArrowRight, Share2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Helmet } from "react-helmet-async";
import { trackEvent } from "../hooks/useAnalytics";
import { convertDropboxUrl, convertDropboxUrlsInHtml } from "../utils/dropboxUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extract headings from HTML content for Table of Contents
const extractHeadings = (htmlContent) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const headings = [];
  
  doc.querySelectorAll('h2, h3').forEach((heading, index) => {
    const id = `heading-${index}`;
    const level = parseInt(heading.tagName.charAt(1));
    headings.push({
      id,
      text: heading.textContent,
      level
    });
  });
  
  return headings;
};

// Add IDs to headings in content
const addHeadingIds = (htmlContent) => {
  let index = 0;
  return htmlContent.replace(/<(h[23])([^>]*)>/gi, (match, tag, attrs) => {
    const id = `heading-${index++}`;
    return `<${tag}${attrs} id="${id}">`;
  });
};

// Get reading time
const getReadingTime = (content) => {
  const text = content.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).length;
  return Math.ceil(words / 200);
};

const TableOfContents = ({ headings }) => {
  if (headings.length === 0) return null;
  
  return (
    <nav className="bg-[#171717] border border-white/10 p-6 mb-8" data-testid="table-of-contents">
      <h3 className="font-anton text-lg text-white mb-4">Table of Contents</h3>
      <ul className="space-y-2">
        {headings.map((heading) => (
          <li 
            key={heading.id}
            style={{ marginLeft: heading.level === 3 ? '1rem' : 0 }}
          >
            <a 
              href={`#${heading.id}`}
              className="text-white/70 hover:text-[#EA580C] text-sm transition-colors"
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/articles/${slug}`);
        setArticle(response.data);
        
        // Track page view
        trackEvent('page_view', `/artiklar/${slug}`);
        
        // Fetch related articles
        const articlesRes = await axios.get(`${API}/articles`);
        const related = articlesRes.data.articles
          .filter(a => a.slug !== slug)
          .slice(0, 3);
        setRelatedArticles(related);
      } catch (e) {
        setError(e.response?.status === 404 ? "Article not found" : "Failed to load article");
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const headings = useMemo(() => {
    if (!article) return [];
    return extractHeadings(article.content);
  }, [article]);

  const processedContent = useMemo(() => {
    if (!article) return '';
    let content = addHeadingIds(article.content);
    // Convert any Dropbox URLs in the content
    content = convertDropboxUrlsInHtml(content);
    return content;
  }, [article]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: url
        });
      } catch (e) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#EA580C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-anton text-4xl text-white mb-4">Article Not Found</h1>
          <p className="text-white/60 mb-8">{error || "The article you're looking for doesn't exist."}</p>
          <Link to="/artiklar" className="text-[#EA580C] hover:underline">
            ← Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  const readingTime = getReadingTime(article.content);
  const articleUrl = `${window.location.origin}/artiklar/${article.slug}`;

  return (
    <>
      <Helmet>
        <title>{`${article.meta_title || article.title} | Arctic Riders`}</title>
        <meta name="description" content={article.meta_description || article.excerpt} />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={articleUrl} />
        {article.featured_image && <meta property="og:image" content={convertDropboxUrl(article.featured_image)} />}
        <meta property="article:published_time" content={article.created_at} />
        {article.updated_at && <meta property="article:modified_time" content={article.updated_at} />}
        <meta property="article:author" content={article.author} />
        <link rel="canonical" href={articleUrl} />
        
        {/* Schema.org Article markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.featured_image || "",
            "datePublished": article.created_at,
            "dateModified": article.updated_at || article.created_at,
            "author": {
              "@type": "Organization",
              "name": article.author || "Arctic Riders"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Arctic Riders",
              "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_010ae1da-d261-457b-bc6f-35db86b75c89/artifacts/31d9ktxd_Logga%20Ligande.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": articleUrl
            },
            "keywords": article.tags ? article.tags.join(", ") : ""
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A]">
        {/* Header */}
        <header className="bg-[#171717] border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/artiklar" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-manrope">All Articles</span>
            </Link>
            <Link to="/" className="font-anton text-xl text-white">
              ARCTIC RIDERS
            </Link>
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              data-testid="share-button"
            >
              <Share2 className="w-5 h-5" />
              <span className="hidden sm:inline font-manrope">Share</span>
            </button>
          </div>
        </header>

        {/* Featured Image */}
        {article.featured_image && (
          <div className="w-full h-[40vh] md:h-[50vh] relative">
            <img 
              src={convertDropboxUrl(article.featured_image)} 
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
          </div>
        )}

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          {/* Article Header */}
          <header className={article.featured_image ? "-mt-32 relative z-10" : ""}>
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1 bg-[#2e3104] text-[#EA580C] text-xs uppercase tracking-wider"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            
            <h1 className="font-anton text-3xl md:text-5xl text-white mb-6">
              {article.title}
            </h1>
            
            <div className="flex items-center gap-6 text-white/60 text-sm mb-8 pb-8 border-b border-white/10">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(parseISO(article.created_at), "MMMM d, yyyy")}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {readingTime} min read
              </span>
              <span>By {article.author}</span>
            </div>
          </header>

          {/* Excerpt */}
          <p className="text-white/80 text-lg font-manrope leading-relaxed mb-8 italic">
            {article.excerpt}
          </p>

          {/* Table of Contents */}
          <TableOfContents headings={headings} />

          {/* Main Content */}
          <div 
            className="article-content prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: processedContent }}
            data-testid="article-content"
          />
        </article>

        {/* CTA Section */}
        <section className="bg-[#171717] py-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-anton text-3xl text-white mb-4">
              Redo att köra Stockholm?
            </h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">
              Uppleva äventyret själv. Hyr en motorcykel eller följ med på en guidad tur
              genom Stockholms varierande landskap.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-block bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-4 font-semibold uppercase tracking-wider transition-colors"
                data-testid="article-cta-book"
              >
                Boka en MC
              </Link>
              <Link
                to="/artiklar"
                className="inline-block bg-transparent border border-white/30 hover:border-white text-white px-8 py-4 font-semibold uppercase tracking-wider transition-colors"
              >
                Fler artiklar
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-6xl mx-auto px-6 py-16">
            <h2 className="font-anton text-2xl text-white mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link 
                  key={related.id}
                  to={`/artiklar/${related.slug}`}
                  className="group bg-[#171717] border border-white/5 hover:border-[#EA580C]/30 transition-all"
                  data-testid={`related-article-${related.slug}`}
                >
                  {related.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={convertDropboxUrl(related.featured_image)} 
                        alt={related.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-anton text-lg text-white group-hover:text-[#EA580C] transition-colors">
                      {related.title}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-[#EA580C] text-sm mt-2">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="bg-[#0A0A0A] border-t border-white/10 py-8 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Arctic Riders · Org.nr 559205-3432 · Motorcykeläventyr i Stockholm.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ArticlePage;
